<template>
  <div v-if="notif" class="ann-overlay" :class="position === 'center' ? 'ann-center' : 'ann-corner'">
    <div v-if="position === 'center'" class="ann-backdrop" @click="dismissible && close()"></div>
    <div class="ann-card" :class="'anim-' + animation" :style="cardStyle">
      <div v-if="confetti" class="ann-confetti">
        <i v-for="n in 24" :key="n" class="ann-confetti-piece" :style="confettiStyle(n)"></i>
      </div>
      <button v-if="dismissible" class="ann-close" :style="{ color: textColor }" @click="close()">&times;</button>
      <div v-if="imageUrl" class="ann-image" :style="{ backgroundImage: 'url(' + imageUrl + ')' }"></div>
      <div class="ann-body">
        <h3 class="ann-title" :style="{ color: textColor }">{{ notif.title }}</h3>
        <div class="ann-content" v-html="notif.content"></div>
        <button v-if="ctaText" class="ann-cta" :style="ctaStyleObj" @click="cta()">{{ ctaText }}</button>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'AnnouncementPopup',
    data: () => ({ notif: null }),
    computed: {
      detail() { return (this.notif && this.notif.detail) || {} },
      animation() { return this.detail.animation || 'fade' },
      position() { return this.detail.position || 'center' },
      imageUrl() { return this.detail.imageUrl || this.detail.image_url },
      bgColor() { return this.detail.bgColor || this.detail.bg_color || '#0F172A' },
      textColor() { return this.detail.textColor || this.detail.text_color || '#FFFFFF' },
      accentColor() { return this.detail.accentColor || this.detail.accent_color || '#6366F1' },
      ctaText() { return this.detail.ctaText || this.detail.cta_text },
      ctaLink() { return this.detail.ctaLink || this.detail.cta_link },
      dismissible() { return this.detail.dismissible !== false },
      confetti() { return !!this.detail.confetti },
      frequency() { return this.detail.frequency || 'once' },
      cardStyle() { return { backgroundColor: this.bgColor, color: this.textColor } },
      ctaStyleObj() {
        const outline = (this.detail.ctaStyle || this.detail.cta_style) === 'outline'
        return outline
          ? { border: '2px solid ' + this.accentColor, color: this.accentColor, background: 'transparent' }
          : { backgroundColor: this.accentColor, color: '#fff' }
      },
    },
    created() { this.load() },
    methods: {
      confettiStyle(n) {
        const i = n - 1
        return {
          left: ((i * 4.17) % 100) + '%',
          backgroundColor: i % 2 ? this.accentColor : '#FFFFFF',
          animationDelay: ((i % 8) * 0.05) + 's',
          animationDuration: (1.6 + (i % 5) * 0.2) + 's'
        }
      },
      load() {
        this.axios.get('notifications?per_page=50')
          .then((res) => {
            const list = (res && res.data) || []
            const now = Date.now()
            const session = this.sessionDismissed()
            this.notif = list.find((n) => {
              const d = (n && n.detail) || {}
              const dt = d.displayType || d.display_type
              if (dt !== 'popup') return false
              if (!this.withinWindow(d, now)) return false
              const freq = d.frequency || 'once'
              if (freq === 'session') return session.indexOf(n.id) === -1
              return !n.read
            }) || null
            if (this.notif) this.track(this.notif.id, 'viewed')
          })
          .catch(() => {})
      },
      withinWindow(d, now) {
        const start = d.startsAt || d.starts_at
        const end = d.expiresAt || d.expires_at
        if (start && now < new Date(start).getTime()) return false
        if (end && now > new Date(end).getTime()) return false
        return true
      },
      sessionDismissed() {
        try { return JSON.parse(sessionStorage.getItem('brobot_dismissed_popups') || '[]') } catch (e) { return [] }
      },
      addSessionDismissed(id) {
        const ids = this.sessionDismissed()
        if (ids.indexOf(id) === -1) {
          ids.push(id)
          sessionStorage.setItem('brobot_dismissed_popups', JSON.stringify(ids))
        }
      },
      close() {
        const notif = this.notif
        if (!notif) return
        this.track(notif.id, 'dismissed')
        if (this.frequency === 'session') this.addSessionDismissed(notif.id)
        else this.axios.put('notifications/' + notif.id, { read: true }).catch(() => {})
        this.notif = null
      },
      cta() {
        const notif = this.notif
        if (!notif) return
        this.track(notif.id, 'cta_clicked')
        if (this.ctaLink) window.open(this.ctaLink, '_blank', 'noopener')
        this.close()
      },
      track(id, event) {
        this.axios.post('notifications/' + id + '/events', { event }).catch(() => {})
      },
    },
  }
</script>

<style scoped>
  .ann-overlay { position: fixed; z-index: 3000; }
  .ann-center { inset: 0; display: flex; align-items: center; justify-content: center; padding: 16px; }
  .ann-corner { bottom: 24px; right: 24px; }
  .ann-backdrop { position: absolute; inset: 0; background: rgba(0, 0, 0, 0.5); }
  .ann-card {
    position: relative; width: 100%; max-width: 420px; border-radius: 16px; overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  }
  .ann-close {
    position: absolute; top: 10px; right: 12px; z-index: 2; border: 0; background: transparent;
    font-size: 24px; line-height: 1; cursor: pointer; opacity: 0.7;
  }
  .ann-close:hover { opacity: 1; }
  .ann-image { height: 160px; width: 100%; background-size: cover; background-position: center; }
  .ann-confetti { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 3; }
  .ann-confetti-piece {
    position: absolute; top: -20px; width: 8px; height: 8px; border-radius: 2px;
    animation-name: annConfetti; animation-timing-function: ease-in; animation-fill-mode: both;
  }
  @keyframes annConfetti {
    from { transform: translateY(0) rotate(0); opacity: 1; }
    to { transform: translateY(340px) rotate(360deg); opacity: 0; }
  }
  .ann-body { padding: 24px; }
  .ann-title { font-size: 20px; font-weight: 700; margin: 0 0 8px; }
  .ann-content { font-size: 14px; opacity: 0.92; }
  .ann-content >>> a { text-decoration: underline; }
  .ann-cta {
    margin-top: 20px; display: inline-flex; align-items: center; border: 0;
    border-radius: 8px; padding: 8px 16px; font-size: 14px; font-weight: 600; cursor: pointer;
  }
  .anim-fade { animation: annFade 0.35s ease both; }
  .anim-slide-up { animation: annSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) both; }
  .anim-slide-right { animation: annSlideRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) both; }
  .anim-zoom { animation: annZoom 0.35s cubic-bezier(0.16, 1, 0.3, 1) both; }
  .anim-bounce { animation: annBounce 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
  .anim-flip { animation: annFlip 0.5s cubic-bezier(0.16, 1, 0.3, 1) both; }
  @keyframes annFade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes annSlideUp { from { opacity: 0; transform: translateY(48px); } to { opacity: 1; transform: none; } }
  @keyframes annSlideRight { from { opacity: 0; transform: translateX(-48px); } to { opacity: 1; transform: none; } }
  @keyframes annZoom { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
  @keyframes annBounce { 0% { opacity: 0; transform: scale(0.6); } 60% { opacity: 1; transform: scale(1.05); } 100% { transform: scale(1); } }
  @keyframes annFlip { from { opacity: 0; transform: perspective(600px) rotateX(-90deg); } to { opacity: 1; transform: perspective(600px) rotateX(0); } }
</style>
